/**
 * Phase 4 plan 04-06 — UI-facing wrapper around the raw `llmApiKeyService`.
 *
 * Adds two UX-focused behaviours on top of the thin HTTP client:
 *   1. `createLlmApiKey` translates a 422 save-anyway response (D-20) into a
 *      discriminated union so the caller can prompt the user without catching.
 *   2. `testLlmApiKey` translates a 429 (D-24) into a typed `RateLimitError`
 *      so the UI layer can show a retry-after toast.
 *
 * The rest of the CRUD surface delegates to the existing singular service.
 */
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { llmApiKeyService } from './llm-api-key'
import type {
  LLMAPIKey,
  LLMAPIKeyCreateRequest,
  LLMAPIKeyCreateResponse,
  LLMAPIKeyListResponse,
  LLMAPIKeyTestResponse,
  LLMAPIKeyUpdateRequest,
  LLMProviderValue,
} from '@/schemas/llm-api-key'

/** Backend 422 payload for the save-anyway fork (D-20). */
interface SaveAnywayDetail {
  save_anyway: true
  provider: LLMProviderValue | string
  error: string
}

interface SaveAnywayResponseShape {
  detail?: SaveAnywayDetail | string
}

/** Backend 429 payload for the org-scoped rate limiter (D-24). */
interface RateLimitDetail {
  retry_after?: number
  limit?: number
  window?: string
}

interface RateLimitResponseShape {
  detail?: RateLimitDetail | string
}

export class RateLimitError extends Error {
  readonly retryAfter: number
  readonly limit: number
  readonly window: string

  constructor(retryAfter: number, limit: number, window: string) {
    super('Rate limit exceeded')
    this.name = 'RateLimitError'
    this.retryAfter = retryAfter
    this.limit = limit
    this.window = window
  }
}

/** Discriminated union for `createLlmApiKey`. */
export type CreateLlmApiKeyResult =
  | { ok: true; key: LLMAPIKey }
  | { ok: false; saveAnyway: true; provider: string; error: string }

export async function listLlmApiKeys(
  orgId: string,
  provider?: LLMProviderValue,
  config?: AxiosRequestConfig,
): Promise<LLMAPIKeyListResponse> {
  return llmApiKeyService.listApiKeys(orgId, provider, config)
}

export async function createLlmApiKey(
  orgId: string,
  input: LLMAPIKeyCreateRequest,
  config?: AxiosRequestConfig,
): Promise<CreateLlmApiKeyResult> {
  try {
    const response: LLMAPIKeyCreateResponse =
      await llmApiKeyService.createApiKey(orgId, input, config)
    return { ok: true, key: response.api_key }
  } catch (error) {
    const axiosError = error as AxiosError<SaveAnywayResponseShape>
    if (axiosError.response?.status === 422) {
      const detail = axiosError.response.data?.detail
      if (detail && typeof detail === 'object' && detail.save_anyway === true) {
        return {
          ok: false,
          saveAnyway: true,
          provider: String(detail.provider ?? input.provider),
          error: detail.error,
        }
      }
    }
    throw error
  }
}

export async function testLlmApiKey(
  orgId: string,
  apiKeyId: string,
  config?: AxiosRequestConfig,
): Promise<LLMAPIKeyTestResponse> {
  try {
    return await llmApiKeyService.testSavedApiKey(orgId, apiKeyId, config)
  } catch (error) {
    const axiosError = error as AxiosError<RateLimitResponseShape>
    if (axiosError.response?.status === 429) {
      const detail = axiosError.response.data?.detail
      if (detail && typeof detail === 'object') {
        throw new RateLimitError(
          Number(detail.retry_after ?? 60),
          Number(detail.limit ?? 10),
          String(detail.window ?? '1m'),
        )
      }
      throw new RateLimitError(60, 10, '1m')
    }
    throw error
  }
}

export async function setDefaultLlmApiKey(
  orgId: string,
  apiKeyId: string,
  config?: AxiosRequestConfig,
): Promise<LLMAPIKey> {
  const update: LLMAPIKeyUpdateRequest = { is_default: true }
  return llmApiKeyService.updateApiKey(orgId, apiKeyId, update, config)
}

export async function deleteLlmApiKey(
  orgId: string,
  apiKeyId: string,
  config?: AxiosRequestConfig,
): Promise<void> {
  await llmApiKeyService.deleteApiKey(orgId, apiKeyId, config)
}
